import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import { isBundleCartItem } from '../lib/cart';
import CheckoutContext from '../lib/CheckoutContext';
import { centsToDollarsNumber, getLocalizedPricing } from '../lib/util/money';
import * as types from '../types';
import useProductSkuMap from './useProductSkuMap';

/**
 * The price a rescue membership renews onto once the gifted months run out, formatted for the
 * active locale, or null when no such plan is in the product payload.
 *
 * Resolved from the plan actually in the cart - its 12-month sibling in the same plan family - so
 * the figure tracks product config and currency instead of a literal. `web` hardcodes "$189/yr" in
 * the `/rescue/:slug` offer string, and that is the mistake this exists to avoid repeating: callers
 * that cannot get a price should drop the disclosure rather than quote a number nothing backs.
 */
export default function useRescueRenewalPrice(): string | null {
  const { cart } = useContext(CheckoutContext);
  const productSkuMap = useProductSkuMap();
  const products = useSelector((state: types.AppState) => state.config.products);
  const { currency, wholeNumberCurrencyFormatter } = useLocale();

  return useMemo(() => {
    const cartPlan = Object.values(cart.cartItems)
      .filter(isBundleCartItem)
      .map((cartItem) => productSkuMap.get(cartItem.subscriptionLineItem.sku)?.product)
      .find(
        (product): product is types.ISubscriptionProduct => !!product && types.isModuleSubscriptionProduct(product),
      );

    if (!cartPlan) {
      return null;
    }

    const yearlyPlan = products
      .filter(types.isModuleSubscriptionProduct)
      .find(
        (plan) =>
          plan.renewalMonths === 12 &&
          !plan.freeTrial &&
          !plan.thirdParty &&
          !plan.isPlusPlan === !cartPlan.isPlusPlan &&
          plan.canBePurchasedForProductIds.some((id) => cartPlan.canBePurchasedForProductIds.includes(id)),
      );

    return yearlyPlan
      ? wholeNumberCurrencyFormatter.format(
          centsToDollarsNumber(getLocalizedPricing(yearlyPlan.pricingInCents, currency)),
        )
      : null;
  }, [cart.cartItems, productSkuMap, products, currency, wholeNumberCurrencyFormatter]);
}
